// src/components/AdvancedIntegrationsSection.js
"use client";
import React, { useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Stack,
  Paper,
  TextField,
  Button,
  Tooltip,
  Chip,
  Divider,
  Grid,
  IconButton,
  Alert,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LockIcon from "@mui/icons-material/Lock";
import ApiIcon from "@mui/icons-material/Api";
import StorageIcon from "@mui/icons-material/Storage";
import FeedbackIcon from "@mui/icons-material/Feedback";
import SettingsEthernetIcon from "@mui/icons-material/SettingsEthernet";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import SecurityIcon from "@mui/icons-material/Security";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import BoltIcon from "@mui/icons-material/Bolt";

/* Helper: subtle premium chip */
const PremiumChip = ({ isPlatinum }) => (
  <Chip
    icon={isPlatinum ? <BoltIcon /> : <LockIcon />}
    label={isPlatinum ? "PLATINUM" : "Premium (view only)"}
    size="small"
    sx={{
      fontWeight: 700,
      textTransform: "uppercase",
      bgcolor: (t) => (isPlatinum ? alpha(t.palette.warning.main, 0.2) : "transparent"),
      color: (t) => (isPlatinum ? t.palette.warning.dark : t.palette.text.secondary),
      border: (t) => `1px solid ${isPlatinum ? alpha(t.palette.warning.main, 0.6) : t.palette.divider}`,
    }}
    variant={isPlatinum ? "filled" : "outlined"}
  />
);

/* Helper: section header */
const SectionHeader = ({ icon, title, count }) => (
  <Stack direction="row" spacing={1} alignItems="center">
    {icon}
    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
    <Chip
      label={`${count} configured`}
      size="small"
      color="primary"
      variant="outlined"
    />
  </Stack>
);

/* Helper: disables content when not platinum but keeps it visible */
const DisabledWrap = ({ disabled, children }) => (
  <Box sx={{ opacity: disabled ? 0.65 : 1, pointerEvents: disabled ? "none" : "auto" }}>
    {children}
  </Box>
);

const AdvancedIntegrationsSection = ({ userPlan = "basic" }) => {
  const theme = useTheme();

  const isPlatinum = String(userPlan || "").toUpperCase() === "PLATINUM";
  const disabled = !isPlatinum;

  /* State per integration type (UI only; wire to API later) */
  const [apis, setApis] = useState([]);
  const [dbs, setDbs] = useState([]);
  const [crms, setCrms] = useState([]);
  const [connectors, setConnectors] = useState([]);
  const [mailboxes, setMailboxes] = useState([]);

  /* Add / remove generic */
  const addItem = (type, template = {}) => {
    const item = { id: Date.now(), ...template };
    if (type === "api") setApis((s) => [...s, item]);
    if (type === "db") setDbs((s) => [...s, item]);
    if (type === "crm") setCrms((s) => [...s, item]);
    if (type === "connector") setConnectors((s) => [...s, item]);
    if (type === "mail") setMailboxes((s) => [...s, item]);
  };
  const removeItem = (type, id) => {
    const cut = (fn) => fn((prev) => prev.filter((x) => x.id !== id));
    if (type === "api") return cut(setApis);
    if (type === "db") return cut(setDbs);
    if (type === "crm") return cut(setCrms);
    if (type === "connector") return cut(setConnectors);
    if (type === "mail") return cut(setMailboxes);
  };

  /* Top container (subtle premium framing) */
  return (
    <Paper
      elevation={0}
      sx={{
        p: 0,
        mb: 3,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
      }}
    >
      {/* <Box
        sx={{
          px: 2.5,
          py: 1.75,
          borderBottom: "1px solid",
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          bgcolor: (t) => alpha(t.palette.warning.main, 0.06),
        }}
      > */}
        {/* <SecurityIcon sx={{ color: (t) => t.palette.warning.dark }} />
        <Typography variant="h6" sx={{ fontWeight: 900, letterSpacing: 0.25 }}>
          Advanced Integrations
        </Typography>
        <PremiumChip isPlatinum={isPlatinum} /> */}
        {/* <Box sx={{ flexGrow: 1 }} />
        {!isPlatinum && (
          <Typography variant="caption" color="text.secondary">
            View what Platinum offers. Upgrade to enable editing.
          </Typography>
        )}
      </Box> */}

      <Box sx={{ p: 2.5 }}>
        {!isPlatinum && (
          <Alert severity="info" sx={{ mb: 2 }}>
            You’re on <b>{String(userPlan)}</b>. <b>Platinum</b> unlocks live API/DB/CRM/Mailbox connectors, secure key vault, polling/webhooks, and training pipelines.
          </Alert>
        )}

        {/* =================== APIs =================== */}
        <Accordion defaultExpanded disableGutters sx={{ mb: 1.5, "&:before": { display: "none" } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <SectionHeader icon={<ApiIcon color="primary" />} title="External API Endpoints" count={apis.length} />
          </AccordionSummary>
          <AccordionDetails>
            <DisabledWrap disabled={disabled}>
              <Stack spacing={2}>
                {apis.map((api) => (
                  <Paper key={api.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>API Connection</Typography>
                      <Tooltip title="Remove">
                        <IconButton size="small" color="error" onClick={() => removeItem("api", api.id)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>

                    <Grid container spacing={1} sx={{ mt: 0.5 }}>
                      <Grid item xs={12} md={7}>
                        <TextField label="Base URL" size="small" fullWidth placeholder="https://api.company.com" />
                      </Grid>
                      <Grid item xs={12} md={5}>
                        <TextField
                          select
                          SelectProps={{ native: true }}
                          label="Auth Type"
                          size="small"
                          fullWidth
                        >
                          <option value="none">None</option>
                          <option value="api_key">API Key (Header)</option>
                          <option value="bearer">Bearer Token</option>
                          <option value="basic">Basic Auth</option>
                          <option value="oauth2_client">OAuth2 (Client Credentials)</option>
                          <option value="oauth2_authcode">OAuth2 (Auth Code)</option>
                        </TextField>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField label="Header Name (for API Key)" size="small" fullWidth placeholder="Authorization or X-API-Key" />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField label="Token / Key / Secret" type="password" size="small" fullWidth />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField label="OAuth Client ID (optional)" size="small" fullWidth />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField label="OAuth Client Secret (optional)" type="password" size="small" fullWidth />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField label="OAuth Scopes (comma-separated)" size="small" fullWidth placeholder="read,write" />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField label="OAuth Redirect URI (Auth Code)" size="small" fullWidth placeholder="https://app.yourdomain.com/oauth/callback" />
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <TextField label="Pagination Param (page)" size="small" fullWidth placeholder="page" />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField label="Page Size Param (limit)" size="small" fullWidth placeholder="limit" />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField label="Page Size Value" size="small" fullWidth placeholder="100" />
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <TextField label="Rate Limit: req/min" size="small" fullWidth placeholder="60" />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField label="Retry (max attempts)" size="small" fullWidth placeholder="3" />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField label="Timeout (ms)" size="small" fullWidth placeholder="10000" />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField label="Default Headers (JSON)" size="small" fullWidth placeholder='{"Accept":"application/json"}' />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField label="Incremental Cursor Field (optional)" size="small" fullWidth placeholder="updated_at / modified" />
                      </Grid>
                    </Grid>

                    <Stack direction="row" spacing={1.5} sx={{ mt: 1.5 }}>
                      <Button variant="contained" size="small" startIcon={<CloudSyncIcon />}>Test Connection</Button>
                      <Button variant="outlined" size="small">Save</Button>
                    </Stack>
                  </Paper>
                ))}

                <Button
                  startIcon={<AddCircleOutlineIcon />}
                  variant="outlined"
                  onClick={() => addItem("api")}
                >
                  Add API Connection
                </Button>
              </Stack>
            </DisabledWrap>
          </AccordionDetails>
        </Accordion>

        {/* =================== Databases / Warehouses =================== */}
        <Accordion disableGutters sx={{ mb: 1.5, "&:before": { display: "none" } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <SectionHeader icon={<StorageIcon color="primary" />} title="Databases / Warehouses" count={dbs.length} />
          </AccordionSummary>
          <AccordionDetails>
            <DisabledWrap disabled={disabled}>
              <Stack spacing={2}>
                {dbs.map((db) => (
                  <Paper key={db.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Database Connection</Typography>
                      <Tooltip title="Remove">
                        <IconButton size="small" color="error" onClick={() => removeItem("db", db.id)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>

                    <Grid container spacing={1} sx={{ mt: 0.5 }}>
                      <Grid item xs={12} md={4}>
                        <TextField select SelectProps={{ native: true }} label="Type" size="small" fullWidth>
                          <option value="postgres">PostgreSQL</option>
                          <option value="mysql">MySQL</option>
                          <option value="mssql">SQL Server</option>
                          <option value="oracle">Oracle</option>
                          <option value="snowflake">Snowflake</option>
                          <option value="bigquery">BigQuery</option>
                          <option value="mongodb">MongoDB</option>
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField label="Host / Project" size="small" fullWidth />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField label="Port" size="small" fullWidth placeholder="5432" />
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <TextField label="Database / Dataset" size="small" fullWidth />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField label="Username" size="small" fullWidth />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField label="Password / Key" type="password" size="small" fullWidth />
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <TextField label="Schema (optional)" size="small" fullWidth />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField label="Tables Include (regex)" size="small" fullWidth placeholder="^public\..*" />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField label="Tables Exclude (regex)" size="small" fullWidth />
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <TextField label="SSL Mode" size="small" fullWidth placeholder="require/prefer/disable" />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField label="Read Only Role" size="small" fullWidth placeholder="readonly" />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField label="Max Pool Size" size="small" fullWidth placeholder="5" />
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          label="Extraction Query (optional)"
                          size="small"
                          fullWidth
                          placeholder="SELECT * FROM public.docs WHERE updated_at > :cursor"
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField label="Cursor Column (for incremental sync)" size="small" fullWidth placeholder="updated_at" />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField label="Batch Size" size="small" fullWidth placeholder="1000" />
                      </Grid>
                    </Grid>

                    <Stack direction="row" spacing={1.5} sx={{ mt: 1.5 }}>
                      <Button variant="contained" size="small" startIcon={<CloudSyncIcon />}>Test & Connect</Button>
                      <Button variant="outlined" size="small">Save</Button>
                    </Stack>
                  </Paper>
                ))}
                <Button startIcon={<AddCircleOutlineIcon />} variant="outlined" onClick={() => addItem("db")}>
                  Add Database
                </Button>
              </Stack>
            </DisabledWrap>
          </AccordionDetails>
        </Accordion>

        {/* =================== CRM / Feedback =================== */}
        <Accordion disableGutters sx={{ mb: 1.5, "&:before": { display: "none" } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <SectionHeader icon={<FeedbackIcon color="primary" />} title="CRM / Feedback Systems" count={crms.length} />
          </AccordionSummary>
          <AccordionDetails>
            <DisabledWrap disabled={disabled}>
              <Stack spacing={2}>
                {crms.map((crm) => (
                  <Paper key={crm.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>CRM Integration</Typography>
                      <Tooltip title="Remove">
                        <IconButton size="small" color="error" onClick={() => removeItem("crm", crm.id)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>

                    <Grid container spacing={1} sx={{ mt: 0.5 }}>
                      <Grid item xs={12} md={4}>
                        <TextField select SelectProps={{ native: true }} label="Platform" size="small" fullWidth>
                          <option value="hubspot">HubSpot</option>
                          <option value="salesforce">Salesforce</option>
                          <option value="zendesk">Zendesk</option>
                          <option value="freshdesk">Freshdesk</option>
                          <option value="custom">Custom REST</option>
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField label="OAuth Client ID / API Key" size="small" fullWidth />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField label="OAuth Client Secret" type="password" size="small" fullWidth />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField label="Objects (comma-separated)" size="small" fullWidth placeholder="contacts, companies, tickets" />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField label="Fields to Pull (comma-separated)" size="small" fullWidth placeholder="name,email,phone,updated_at" />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField label="Webhook URL (optional)" size="small" fullWidth placeholder="https://app.yourdomain.com/webhooks/crm" />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField label="Webhook Secret (optional)" size="small" fullWidth />
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <TextField label="Polling Interval (min)" size="small" fullWidth placeholder="15" />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField label="Cursor Field" size="small" fullWidth placeholder="updated_at" />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField label="Batch Size" size="small" fullWidth placeholder="500" />
                      </Grid>
                    </Grid>

                    <Stack direction="row" spacing={1.5} sx={{ mt: 1.5 }}>
                      <Button variant="contained" size="small" startIcon={<CloudSyncIcon />}>Connect</Button>
                      <Button variant="outlined" size="small">Save</Button>
                    </Stack>
                  </Paper>
                ))}
                <Button startIcon={<AddCircleOutlineIcon />} variant="outlined" onClick={() => addItem("crm")}>
                  Add CRM Integration
                </Button>
              </Stack>
            </DisabledWrap>
          </AccordionDetails>
        </Accordion>

        {/* =================== Mailboxes (NEW) =================== */}
        <Accordion disableGutters sx={{ mb: 1.5, "&:before": { display: "none" } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <SectionHeader icon={<MailOutlineIcon color="primary" />} title="Mailboxes" count={mailboxes.length} />
          </AccordionSummary>
          <AccordionDetails>
            <DisabledWrap disabled={disabled}>
              <Stack spacing={2}>
                {mailboxes.map((mb) => (
                  <Paper key={mb.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Mailbox Connection</Typography>
                      <Tooltip title="Remove">
                        <IconButton size="small" color="error" onClick={() => removeItem("mail", mb.id)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>

                    <Grid container spacing={1} sx={{ mt: 0.5 }}>
                      <Grid item xs={12} md={4}>
                        <TextField select SelectProps={{ native: true }} label="Provider" size="small" fullWidth>
                          <option value="gmail_oauth">Gmail (OAuth)</option>
                          <option value="outlook_oauth">Outlook / Microsoft 365 (OAuth)</option>
                          <option value="imap">Generic IMAP</option>
                          <option value="pop3">Generic POP3</option>
                        </TextField>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField label="Email Address" size="small" fullWidth placeholder="support@yourdomain.com" />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField label="Display Name" size="small" fullWidth placeholder="Support Inbox" />
                      </Grid>

                      {/* OAuth fields */}
                      <Grid item xs={12} md={6}>
                        <TextField label="OAuth Client ID (if OAuth)" size="small" fullWidth />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField label="OAuth Client Secret (if OAuth)" type="password" size="small" fullWidth />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField label="OAuth Scopes (comma-separated)" size="small" fullWidth placeholder="https://mail.google.com/,offline_access" />
                      </Grid>

                      {/* IMAP/POP3 specifics */}
                      <Grid item xs={12} md={4}>
                        <TextField label="Server Host (IMAP/POP3)" size="small" fullWidth placeholder="imap.yourdomain.com" />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField label="Server Port" size="small" fullWidth placeholder="993 (IMAP SSL), 995 (POP3 SSL)" />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField label="Username (IMAP/POP3)" size="small" fullWidth />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField label="Password / App Password" type="password" size="small" fullWidth />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField label="Security" size="small" fullWidth placeholder="SSL / TLS / STARTTLS" />
                      </Grid>

                      {/* Ingestion controls */}
                      <Grid item xs={12} md={4}>
                        <TextField label="Polling Interval (min)" size="small" fullWidth placeholder="5" />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField label="Max Emails per Poll" size="small" fullWidth placeholder="200" />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField label="Start From (ISO or 'now')" size="small" fullWidth placeholder="now" />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField label="Folders Include (comma-separated)" size="small" fullWidth placeholder="INBOX,Support" />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField label="Folders Exclude (comma-separated)" size="small" fullWidth placeholder="Spam,Trash" />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField label="From Filter (regex)" size="small" fullWidth placeholder=".*@customer.com$" />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField label="Subject Filter (regex)" size="small" fullWidth placeholder="(ticket|issue|bug|order)" />
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <TextField label="Dedup Key (Message-ID/Hash)" size="small" fullWidth placeholder="Message-ID" />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField label="Attachment Handling" size="small" fullWidth placeholder="ignore/links/inline" />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField label="Max Attachment Size (MB)" size="small" fullWidth placeholder="25" />
                      </Grid>

                      <Grid item xs={12}>
                        <FormControlLabel control={<Switch size="small" defaultChecked={!disabled} disabled={disabled} />} label="Enable Webhook for New Messages" />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField label="Webhook URL (optional)" size="small" fullWidth placeholder="https://app.yourdomain.com/webhooks/mail" />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField label="Webhook Secret (optional)" size="small" fullWidth />
                      </Grid>
                    </Grid>

                    <Stack direction="row" spacing={1.5} sx={{ mt: 1.5 }}>
                      <Button variant="contained" size="small" startIcon={<CloudSyncIcon />}>Authenticate / Test</Button>
                      <Button variant="outlined" size="small">Save</Button>
                    </Stack>
                  </Paper>
                ))}
                <Button
                  startIcon={<AddCircleOutlineIcon />}
                  variant="outlined"
                  onClick={() => addItem("mail")}
                >
                  Add Mailbox
                </Button>
              </Stack>
            </DisabledWrap>
          </AccordionDetails>
        </Accordion>

        {/* =================== Custom Connectors =================== */}
        <Accordion disableGutters sx={{ mb: 1, "&:before": { display: "none" } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <SectionHeader icon={<SettingsEthernetIcon color="primary" />} title="Custom Data Connectors" count={connectors.length} />
          </AccordionSummary>
          <AccordionDetails>
            <DisabledWrap disabled={disabled}>
              <Stack spacing={2}>
                {connectors.map((c) => (
                  <Paper key={c.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Custom Connector</Typography>
                      <Tooltip title="Remove">
                        <IconButton size="small" color="error" onClick={() => removeItem("connector", c.id)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>

                    <Grid container spacing={1} sx={{ mt: 0.5 }}>
                      <Grid item xs={12} md={6}>
                        <TextField label="Endpoint / Webhook URL" size="small" fullWidth placeholder="https://api.yourdomain.com/webhook" />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField label="Signing Secret (optional)" size="small" fullWidth />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField label="Retry (max attempts)" size="small" fullWidth placeholder="3" />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField label="Timeout (ms)" size="small" fullWidth placeholder="10000" />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField label="Rate Limit: req/min" size="small" fullWidth placeholder="120" />
                      </Grid>
                    </Grid>

                    <Stack direction="row" spacing={1.5} sx={{ mt: 1.5 }}>
                      <Button variant="contained" size="small">Register</Button>
                      <Button variant="outlined" size="small">Save</Button>
                    </Stack>
                  </Paper>
                ))}
                <Button startIcon={<AddCircleOutlineIcon />} variant="outlined" onClick={() => addItem("connector")}>
                  Add Custom Connector
                </Button>
              </Stack>
            </DisabledWrap>
          </AccordionDetails>
        </Accordion>

        <Divider sx={{ mt: 2 }} />

        {/* Subtle reassurance about security */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
          <SecurityIcon fontSize="small" sx={{ color: "text.secondary" }} />
          <Typography variant="caption" color="text.secondary">
            Credentials are stored server-side with encryption at rest. Rotation, masking and secrets export can be managed from the Admin Console.
          </Typography>
        </Stack>
      </Box>
    </Paper>
  );
};

export default AdvancedIntegrationsSection;
